import { GSheetOAuthTokenStatus } from "@coeff/api";
import { AuthorizeGSheetContent } from "@coeff/components";
import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { CoeLogo } from "../components";
import { config } from "../config";
import { API_ROUTES, COLORS } from "../constants";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils/contexts";

const AUTH_SUCCESS_REDIRECT_PATH = "/oauth_attempt?status=success&from=webapp";

// In local dev, Google allows http://localhost (any port) as a registered redirect URI, so
// redirect straight back to whatever host/port this page is actually running on instead of
// the shared dev.coefficient.io proxy (which can't see this machine's local oauth_attempt
// record). Every other environment keeps using the provider-registered OAUTH_REDIRECT_URL_BASE
// (see useSlackOAuth.ts for the same base URL used elsewhere).
const OAUTH_REDIRECT_ORIGIN =
  process.env.NODE_ENV === "development"
    ? window.location.origin
    : new URL(config.OAUTH_REDIRECT_URL_BASE).origin;

const WholePageLayout = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid ${COLORS.black15};
  background: ${COLORS.white};
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 0;
  flex: 1;
  background: ${COLORS.white};
`;

/**
 * GSheetReauthPage - Lets the current webapp user (re)grant the Google OAuth permission
 * needed to run scheduled imports/alerts/writebacks against Google Sheets (/reauth/gsheet).
 * Always operates on the logged-in session's own identity — there is no per-user lookup.
 */
export const GSheetReauthPage: React.FC = () => {
  const history = useHistory();
  const userEmail = useSelector((state: AppState) => state.app.email);
  const { apiClient } = useApiContext();
  const track = useTrack();

  const handleSuccess = () => history.replace(AUTH_SUCCESS_REDIRECT_PATH);
  const handleCancel = () => history.goBack();

  return (
    <WholePageLayout>
      <PageHeader>
        <CoeLogo />
      </PageHeader>
      <Container>
        <AuthorizeGSheetContent
          copyContext="webapp"
          oauthTokenStatus={GSheetOAuthTokenStatus.Required}
          email={userEmail}
          clientId={config.GOOGLE_CLIENT_ID}
          redirectUri={`${OAUTH_REDIRECT_ORIGIN}${API_ROUTES.googleAuthCallBack}`}
          api={apiClient}
          track={track}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </Container>
    </WholePageLayout>
  );
};

export default GSheetReauthPage;
